import { GridColDef, GridRenderCellParams, GridValueGetterParams } from '@mui/x-data-grid-pro'
import { gridFormatters, gridValueGetters } from 'src/utils/grid'
import { MCPayment } from 'src/interfaces/mcPayment'
import { PaymentStatusChip } from 'src/components/payment/grid/PaymentStatusChip'
import { Typography } from '@mui/material'
import { PaymentFailureReasonTooltip } from 'src/components/payment/PaymentFailureReasonTooltip'
import tw from 'twin.macro'
import { TitledTooltip } from 'src/components/shared/TitledTooltip'
import { colors } from 'src/theme'
import { RiskMinor } from '@shopify/polaris-icons'
import { PaymentStatus } from '../payment/enums'
import { PayoutMethodTitles } from '../payment/constants'
import { MCPaymentsItemActionButton } from 'src/components/mc-payment/grid/action/MCPaymentsItemActionButton'

const mcPaymentStatusesToShowDetails = [PaymentStatus.Failed, PaymentStatus.AutoRetry]

export const mcPaymentColumns: Array<GridColDef> = [
  {
    field: 'masterContractorExternalId',
    headerName: 'Contractors ID',
    sortable: true,
    flex: 2,
  },
  {
    field: 'name',
    headerName: 'Name',
    flex: 2,
    sortable: true,
    renderCell: (params: GridRenderCellParams<MCPayment>) =>
      params.row.paidInGracePeriod ? (
        <TitledTooltip
          title={params.value}
          content={
            <Typography variant="body2" color={colors.SHADES_GREY_300}>
              This contractor is deactivated but payment was processed because they are still within
              the 5 day grace period. Without reactivation, future payments payments to this
              contract may fail. To continue to pay this contractor, they must update their
              documentation to meet compliance requirements.
            </Typography>
          }
        >
          <div css={tw`flex flex-row items-center`}>
            <RiskMinor width={20} height={20} css={tw`mr-1`} fill={colors.COLORS_RED_500} />
            <Typography
              fontWeight={500}
              variant="subtitle1"
              color={colors.COLORS_RED_500}
              css={tw`ml-2 underline`}
            >
              {params.value}
            </Typography>
          </div>
        </TitledTooltip>
      ) : (
        params.value
      ),
  },
  {
    field: 'uploadedAt',
    headerName: 'Added on',
    flex: 2,
    sortable: true,
    type: 'dateTime',
    valueGetter: gridValueGetters.parseDate,
    valueFormatter: gridFormatters.formatShortDate,
  },
  {
    field: 'status',
    headerName: 'Status',
    flex: 2,
    sortable: true,
    renderCell: (params: GridRenderCellParams<MCPayment>) => (
      <div css={tw`flex items-center`}>
        <PaymentStatusChip status={params.value} />
        {mcPaymentStatusesToShowDetails.includes(params.row.status) &&
          !!params.row.failureReasonType && (
            <PaymentFailureReasonTooltip payment={params.row}>
              <Typography
                fontWeight={500}
                variant="subtitle1"
                color="black"
                css={tw`ml-2 underline`}
              >
                Details
              </Typography>
            </PaymentFailureReasonTooltip>
          )}
      </div>
    ),
  },
  {
    field: 'paidAt',
    headerName: 'Paid on date',
    flex: 2,
    sortable: true,
    type: 'dateTime',
    valueGetter: gridValueGetters.parseDate,
    valueFormatter: gridFormatters.formatShortDate,
  },
  {
    field: 'payoutMethod',
    headerName: 'Payout Method',
    flex: 2,
    valueGetter: (params: GridValueGetterParams<MCPayment>) =>
      (params.row.payoutMethod && PayoutMethodTitles[params.row.payoutMethod]) ||
      params.row.payoutMethod ||
      '-',
    sortable: true,
  },
  {
    field: 'paid_to',
    headerName: 'Paid to',
    flex: 2,
    sortable: false,
    valueGetter: (params: GridValueGetterParams<MCPayment>) =>
      `${params.row.destinationType || ''}${
        params.row.destinationLast4 ? ` •• ${params.row.destinationLast4}` : ''
      }` || '-',
  },
  {
    field: 'amount',
    headerName: 'Amount',
    flex: 1.5,
    sortable: true,
    valueFormatter: gridFormatters.formatCurrency,
  },
  {
    field: 'netAmount',
    headerName: 'Net amount',
    flex: 1.5,
    sortable: true,
    valueFormatter: gridFormatters.formatCurrency,
  },
  {
    field: 'actions',
    headerName: 'Actions',
    flex: 1,
    sortable: false,
    disableExport: true,
    renderCell: (params: GridRenderCellParams<MCPayment>) => (
      <MCPaymentsItemActionButton payment={params.row} />
    ),
  },
]

export const mcPaymentPageSizeOptions = [100, 250, 500, 1000]
